import { LoteNotificacaoProgresso } from '@/types/interfaces/LoteNotificacao'

const STATUS_FINAIS = ['Concluido', 'Erro', 'ConcluidoComErros']

export const loteFinalizado = (status?: string) => !!status && STATUS_FINAIS.includes(status)

/** Arredonda para baixo: 995 de 1000 mostra 99%, não 100% com o lote ainda em processamento. */
export const percentualProcessado = (lote: LoteNotificacaoProgresso | null) => {
  if (!lote || !lote.totalItens) return 0
  return Math.min(100, Math.floor(((lote.processados + lote.erros) / lote.totalItens) * 100))
}

export interface LoteNotificacaoPollingOpcoes {
  buscarLote: (loteId: string) => Promise<{ success?: boolean, data?: LoteNotificacaoProgresso }>,
  aoAtualizar: (lote: LoteNotificacaoProgresso) => void,
  aoFinalizar: (lote: LoteNotificacaoProgresso) => void,
  aoExpirar: () => void,
  intervaloMs?: number,
  duracaoMaximaMs?: number,
  maximoFalhasSeguidas?: number,
}

/**
 * Acompanha um lote por vez. A próxima consulta só é agendada depois da resposta da anterior, e a resposta
 * de um acompanhamento já parado (ou trocado de lote) é descartada.
 */
const useLoteNotificacaoPolling = (opcoes: LoteNotificacaoPollingOpcoes) => {
  const intervaloMs = opcoes.intervaloMs || 3000
  const duracaoMaximaMs = opcoes.duracaoMaximaMs || 30 * 60 * 1000
  // Falha permanente (lote apagado, sem permissão) não deve consultar por 30 min sem avisar.
  const maximoFalhasSeguidas = opcoes.maximoFalhasSeguidas || 10

  let loteId: string | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let inicio = 0
  let acompanhamento = 0
  let falhasSeguidas = 0

  function parar() {
    acompanhamento += 1
    if (timer) clearTimeout(timer)
    timer = null
    loteId = null
  }

  async function consultar(atual: number) {
    if (atual !== acompanhamento || !loteId) return

    if (Date.now() - inicio > duracaoMaximaMs) {
      parar()
      opcoes.aoExpirar()
      return
    }

    let response: { success?: boolean, data?: LoteNotificacaoProgresso } = { success: false }
    try {
      response = await opcoes.buscarLote(loteId)
    } catch (e) {
      console.warn('Falha ao consultar o lote de notificação, nova consulta no próximo ciclo.', e)
    }

    if (atual !== acompanhamento) return

    if (response.success && response.data) {
      falhasSeguidas = 0
      opcoes.aoAtualizar(response.data)
      if (loteFinalizado(response.data.status)) {
        parar()
        opcoes.aoFinalizar(response.data)
        return
      }
    } else {
      falhasSeguidas += 1
      if (falhasSeguidas >= maximoFalhasSeguidas) {
        parar()
        opcoes.aoExpirar()
        return
      }
    }

    // eslint-disable-next-line no-use-before-define
    agendarConsulta(atual)
  }

  function agendarConsulta(atual: number) {
    timer = setTimeout(() => consultar(atual), intervaloMs)
  }

  function iniciar(id: string) {
    parar()
    loteId = id
    inicio = Date.now()
    falhasSeguidas = 0
    agendarConsulta(acompanhamento)
  }

  return {
    iniciar,
    parar,
    loteAcompanhado: () => loteId,
  }
}

export default useLoteNotificacaoPolling
